<script>
  import { format } from 'svelte-i18n';

  import { GoogleMap } from '@client/components/map';

  import { recipientsStore } from '@client/stores/recipients';

  import { ACTIVITY_EVENTS } from '@client/enums';

  import { daysBetweenDates } from '@common/utils/dates';

  import { MapLegend } from '../map-legend';

  export let row = {};
  export let recipientFilter = null;

  const unknown = $format('label.UNKNOWN');

  const MIN_DISTANCE = 0.999999;
  const MAX_DISTANCE = 1.001001;

  /**
   * @typedef {Object} MapMarker
   * @property {string} [avatar]
   * @property {string} title
   * @property {string} content - HTML content for marker popup
   * @property {boolean} proxy
   * @property {string} [objecttype]
   * @property {string} [verb]
   * @property {{ lat: number, lng: number }} position
   */

  /**
   * Current markers to show on the map.
   * @type {MapMarker[]}
   */
  let markers = [];

  /**
   * List of activity event types used to filter visible markers.
   * @type {string[]}
   */
  let mapFilters = Object.keys(ACTIVITY_EVENTS);

  /**
   * Builds a filtered and formatted list of map markers from activity data.
   * @returns {MapMarker[]}
   */
  const generateMapMarkers = () => {
    let activity = row.activity || [];

    if (recipientFilter) {
      activity = activity.filter(({ recipient }) => recipient === recipientFilter);
    }

    return activity
      .filter(({ note }) => note?.coords)
      .map(({ note, recipient, utcts, msgsubject, objecttype, verb, ...rest }) => {
        const { coords = {}, isp = '' } = note;
        const { latitude, longitude } = coords;
        const proxy = /proxy|microsoft|relay/gi.test(isp);
        const realtime = daysBetweenDates(new Date(utcts) || new Date(utcts), new Date()) < 1;
        const realtimeInfo = realtime ? (proxy ? 'realtime-proxy' : 'realtime') : '';
        const event = verb === 'clicked' || verb === 'visited' ? objecttype : verb;
        const subject = msgsubject || '';
        const emailSubject = subject ? `${subject}<br/>` : '';
        const proxied = proxy ? ' (Proxy)<br/>' : '<br/>';
        const recipientCache = $recipientsStore[recipient]?.person;
        const recipientImage = recipientCache?.avatar
          ? `<img src=${recipientCache?.avatar} style="border-radius: 100%; width: 48px; height: auto;" loading="lazy" /><br/>`
          : '';
        const recipientName = recipientCache?.name?.fullName ? `<b>${recipientCache?.name?.fullName}</b><br/>` : '';
        const recicpientEmail = recipient ? `<b>${recipient}</b><br/>` : '';

        if (!utcts) {
          return null;
        }

        return {
          avatar: recipientCache?.avatar || null,
          title: `${recipientCache?.name?.fullName || ''} ${recipient} ${subject} ${realtimeInfo}`,
          content: `<div>
            <div>${recipientImage}</div>
            <div>${recipientName}</div>
            <div>${recicpientEmail}</div>
            <div>${emailSubject}</div>
            <div>${isp} ${proxied}</div>
            <div>${$format(ACTIVITY_EVENTS[event]?.label)}</div>
          </div>`,
          proxy,
          objecttype,
          verb,
          position: {
            lat: latitude * (Math.random() * (MAX_DISTANCE - MIN_DISTANCE) + MIN_DISTANCE),
            lng: longitude * (Math.random() * (MAX_DISTANCE - MIN_DISTANCE) + MIN_DISTANCE)
          }
        };
      });
  };

  $: markers = generateMapMarkers(recipientFilter, row);
</script>

<div class="map">
  <MapLegend bind:mapFilters />

  <GoogleMap
    height="530"
    bind:mapFilters
    useAvatars
    config={{
      streetViewControl: false,
      mapTypeControl: false,
      zoom: 4
    }}
    bind:markers
  />
</div>

<style lang="scss" src="./map.scss"></style>
